/**
 * One coloured chip per drafter topic, each with how many conversations it would list under the queue's other
 * filters. Clicking a chip filters to it; clicking it again (or "All") clears the filter. Topics with nothing in
 * them are hidden, except the one currently selected.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { TOPIC_ORDER, topicLabel, topicTone } from '../../lib/supportDesk'

type Props = {
  /** The queue's filters minus topic/sort/paging: state, publication, channel, assignee, q. */
  filters: URLSearchParams
  value: string
  onChange: (topic: string) => void
}

export function TopicFilter({ filters, value, onChange }: Props) {
  const counts = useQuery<Record<string, number>>({
    queryKey: ['support', 'topic-counts', filters.toString()],
    queryFn: () => api.get<Record<string, number>>(`/api/support/topic-counts?${filters}`),
    retry: false,
    refetchInterval: (q) => (q.state.status === 'error' ? false : 60_000),
  })
  const data = counts.data ?? {}
  const known = new Set(TOPIC_ORDER)
  const topics = [...TOPIC_ORDER, ...Object.keys(data).filter(t => !known.has(t)).sort()]
    .filter(t => (data[t] ?? 0) > 0 || t === value)
  const all = Object.values(data).reduce((a, b) => a + b, 0)
  const base = 'font-mono text-[11px] uppercase tracking-tight-cap px-2 py-1 border rounded-sm whitespace-nowrap transition-opacity'

  return (
    <div role="group" aria-label="Filter by topic" className="flex flex-wrap items-center gap-1.5">
      <button type="button" aria-pressed={value === ''} onClick={() => onChange('')}
        className={`${base} ${value === '' ? 'border-amber-glow text-amber-glow' : 'border-ink-500 text-paper-dim hover:text-paper'}`}>
        All {counts.isSuccess ? all : ''}
      </button>
      {topics.map(t => {
        const on = value === t
        return (
          <button key={t} type="button" aria-pressed={on} onClick={() => onChange(on ? '' : t)}
            className={`${base} ${topicTone(t)} ${on ? 'ring-1 ring-current' : value ? 'opacity-50 hover:opacity-100' : 'hover:opacity-80'}`}>
            {topicLabel(t)} <span className="tabular-nums">{data[t] ?? 0}</span>
          </button>
        )
      })}
    </div>
  )
}
